import {
    Dialog, DialogTitle, DialogContent,
    Button, Stack, Typography, useTheme, IconButton
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useNotification} from "../../utils/hooks/useNotification";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EntityTable, {EntityTableHeaderProps} from "../list/EntityList";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useCategories} from "../../utils/categories/CategoriesContext";
import {useRace} from "../../utils/races/RaceContext";
import Loading from "../status/Loading";
import useTableParams from "../list/useTableParams";
import buildUrl from "../../utils/api/buildUrl";
import useSWR from "swr";
import {BoatInRace} from "../../schemas/boats/boatInRaceSchema";
import {getFetcher, putFetcher} from "../../utils/api/fetchers";
import CheckIcon from '@mui/icons-material/Check';
import {useConfirm} from "material-ui-confirm";
import BoatDisqualificationNoteDialog from "./BoatDisqualificationNoteDialog";
import {getCategoryColor} from "../../utils/categories/categoryColors";

interface BoatDisqualificationDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const BoatDisqualificationDialog: React.FC<BoatDisqualificationDialogProps> = ({
                                                                                   open,
                                                                                   onClose,
                                                                                   onSuccess
                                                                               }) => {
    const {showNotification} = useNotification();
    const confirm = useConfirm();
    const {race, isLoading: isLoadingRace} = useRace();
    const {categories, isLoading: isLoadingCategories} = useCategories();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

    const notDisqualifiedTableId = "notDisqualifiedTableId";
    const {
        sortKeys: skNotDisqualified,
        sortDirections: sdNotDisqualified,
        filters: fNotDisqualified,
        clearFilters: cfNotDisqualified,
    } = useTableParams(notDisqualifiedTableId);

    let urlNotDisqualifiedBoats = buildUrl(`/races/${race?.id}/boats`, {
        sortBy: skNotDisqualified,
        sortDirections: sdNotDisqualified,
        filters: fNotDisqualified
    });
    urlNotDisqualifiedBoats += `&filter:disqualified=false`;
    const {
        data: notDisqualifiedBoatsFiltered,
        mutate
    } = useSWR<BoatInRace[]>(race ? urlNotDisqualifiedBoats : null, getFetcher, {keepPreviousData: true});

    useEffect(() => {
        if (open) {
            mutate();
        }
    }, [open, mutate]);

    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [selectedBoat, setSelectedBoat] = useState<BoatInRace | null>(null);

    if (isLoadingRace || !race || isLoadingCategories || !categories) return <Loading/>

    const handleClose = () => {
        onClose();
        cfNotDisqualified();
        setNoteDialogOpen(false);
        setSelectedBoat(null);
    }

    const handleDisqualifyBoat = async (boat: BoatInRace) => {
        confirm({
            title: "Diskvalifikovat loď",
            description: "Opravdu chcete diskvalifikovat turo loď?",
            confirmationText: "Potvrdit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                setSelectedBoat(boat);
                if (race.rules.startNumbers && !boat.startNumber) {
                    showNotification("Loď nemá vyplněné startovní číslo, nelze vložit poznámku", "info");
                    handlePutData(boat, null);
                } else {
                    setNoteDialogOpen(true);
                }
            })
            .catch(() => {
                setSelectedBoat(null);
            });
    }

    const handlePutData = async (boat: BoatInRace, note: string | null) => {
        putFetcher(`/races/${race.id}/boats/${boat.boatId}/disqualified`, {disqualified: true})
            .then(() => {
                if (note) {
                    putFetcher(`/races/${race.id}/boats/${boat.boatId}`, {
                        startNumber: boat.startNumber ?? null,
                        notes: note
                    })
                        .then(() => {
                            showNotification("Loď byla úspěšně diskvalifikována a poznámka uložena", "success");
                            onSuccess();
                            handleClose();
                        })
                        .catch((error) => {
                            showNotification("Chyba při ukládání poznámky k diskvalifikaci lodi", "error");
                            console.error(error);
                        });
                } else {
                    showNotification("Loď byla úspěšně diskvalifikována", "success");
                    onSuccess();
                    handleClose();
                }
            })
            .catch((error) => {
                showNotification("Chyba při diskvalifikaci lodi", "error");
                console.error(error);
            });
    };

    return (<>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={isSmall}
                maxWidth="lg"
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h5" fontWeight="bold">Výběr lodi k diskvalifikaci</Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleClose}
                            startIcon={<HighlightOffIcon/>}
                        >
                            Zavřít
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack direction="column" spacing={0} flexGrow={1}>
                        <EntityTable
                            tableId={notDisqualifiedTableId}
                            headers={[
                                {key: 'name', label: 'Jméno', sortable: true, filtered: true},
                                {
                                    key: 'category',
                                    label: 'Kategorie',
                                    sortable: true,
                                    filtered: true,
                                    filterConfig: {
                                        type: 'multiSelect',
                                        options: [
                                            ...(categories).map(category => ({
                                                label: category.name,
                                                value: category.categoryNumber.toString()
                                            }))
                                        ]
                                    },
                                    render: (category: any) => (
                                        <span
                                            style={{color: getCategoryColor(category?.categoryNumber), fontWeight: "bold"}}
                                        >
                                            {category?.name}
                                        </span>
                                    )
                                },
                                race.rules.startNumbers ? {
                                    key: 'startNumber',
                                    label: 'Startovní číslo',
                                    sortable: true,
                                    filtered: true,
                                    filterConfig: {
                                        type: 'number',
                                        min: 0
                                    },
                                    render: (type: number) => type ||
                                        <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                                } : {
                                    key: "boatNumber",
                                    label: "Evidenční číslo",
                                    sortable: true,
                                    filtered: true,
                                    filterConfig: {
                                        type: 'number',
                                        min: 0
                                    },
                                    render: (type: string) => type ||
                                        <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                                },
                                {
                                    key: 'controls',
                                    render:
                                        (boat: BoatInRace) => (
                                            <IconButton color="error" title="Diskvalifikovat loď"
                                                        onClick={() => handleDisqualifyBoat(boat)}>
                                                <CheckIcon/>
                                            </IconButton>
                                        )
                                }
                            ] as EntityTableHeaderProps[]}
                            entities={notDisqualifiedBoatsFiltered ?? []}
                            paged={false}
                            indexes
                            entitiesCount={notDisqualifiedBoatsFiltered?.length ?? 0}
                        />
                    </Stack>
                </DialogContent>
            </Dialog>
            <BoatDisqualificationNoteDialog
                open={noteDialogOpen}
                onClose={() => {
                    setNoteDialogOpen(false);
                    setSelectedBoat(null);
                }}
                onSave={(value)=> {
                    setNoteDialogOpen(false);
                    if (selectedBoat) {
                        handlePutData(selectedBoat, value);
                    }
                    setSelectedBoat(null);
                }}
            />
        </>
    );
};

export default BoatDisqualificationDialog;
